import { useCallback, useRef, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { useLazyFetchNetworkGroupValuesQuery } from '@/features/networkGroupSlice';
import type { GroupId } from '@/features/types';

const SEARCH_DEBOUNCE_MS = 500;
const PAGE_LIMIT = 50;
/** Past this many loaded values, stop appending pages and ask the user to narrow. */
const MAX_LOADED_VALUES = 1000;

/**
 * Server-side value search for a `searchable` network group: debounced substring
 * search plus keyset pagination, accumulating pages until the term changes.
 *
 * `initialValues` is the first page inlined by `all_values`; keyset pagination
 * means its last value doubles as the cursor for the next page, and `searchable`
 * guarantees more values exist beyond it.
 */
export const useNetworkGroupValueSearch = (
  groupId: GroupId,
  initialValues: string[] = []
) => {
  const [fetchPage] = useLazyFetchNetworkGroupValuesQuery();
  const [searchValue, setSearchValue] = useState('');
  const [values, setValues] = useState<string[]>(initialValues);
  const [nextCursor, setNextCursor] = useState<string | null>(
    initialValues.length > 0 ? initialValues[initialValues.length - 1] : null
  );
  const [isFetching, setIsFetching] = useState(false);

  /**
   * The term the user currently wants results for. In-flight responses for any
   * other term are dropped, so a slow first page can't clobber newer results.
   */
  const latestTermRef = useRef('');
  const startedRef = useRef(initialValues.length > 0);
  const loadingMoreRef = useRef(false);

  const runSearch = useCallback(
    async (term: string) => {
      if (latestTermRef.current !== term) return;
      setIsFetching(true);
      const page = await fetchPage(
        { groupId, q: term, limit: PAGE_LIMIT },
        true
      )
        .unwrap()
        .catch((): undefined => undefined);
      if (latestTermRef.current !== term) return;
      setIsFetching(false);
      if (!page) return;
      setValues(page.values);
      setNextCursor(page.nextCursor ?? null);
    },
    [fetchPage, groupId]
  );

  const debouncedRunSearch = useDebouncedCallback(
    runSearch,
    SEARCH_DEBOUNCE_MS
  );

  const onSearchChange = useCallback(
    (term: string) => {
      setSearchValue(term);
      latestTermRef.current = term;
      debouncedRunSearch(term);
    },
    [debouncedRunSearch]
  );

  /**
   * Loads the first (browse) page when the dropdown first opens; a no-op when
   * `initialValues` already seeded it.
   */
  const ensureLoaded = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void runSearch(latestTermRef.current);
  }, [runSearch]);

  const atCap = nextCursor !== null && values.length >= MAX_LOADED_VALUES;
  const hasMore = nextCursor !== null && !atCap;

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || nextCursor === null) return;
    if (values.length >= MAX_LOADED_VALUES) return;
    const term = latestTermRef.current;
    loadingMoreRef.current = true;
    setIsFetching(true);
    const page = await fetchPage(
      { groupId, q: term, cursor: nextCursor, limit: PAGE_LIMIT },
      true
    )
      .unwrap()
      .catch((): undefined => undefined);
    loadingMoreRef.current = false;
    // A term change mid-flight means runSearch owns the state now.
    if (latestTermRef.current !== term) return;
    setIsFetching(false);
    if (!page) return;
    setValues((prev) => [...prev, ...page.values]);
    setNextCursor(page.nextCursor ?? null);
  }, [fetchPage, groupId, nextCursor, values.length]);

  return {
    searchValue,
    onSearchChange,
    values,
    isFetching,
    hasMore,
    atCap,
    loadMore,
    ensureLoaded,
  };
};
